import type { FormEvent, ReactNode } from 'react'

import { useId, useState } from 'react'
import { ArrowRight, Check } from 'lucide-react'
import { cn } from '@/lib/utils'

type Status = 'idle' | 'sending' | 'done' | 'error'

interface NewsletterFormProps {
    source?: string
    className?: string
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export const NewsletterForm = ({ source = 'footer', className }: NewsletterFormProps): ReactNode => {
    const inputId = useId()
    const messageId = useId()
    const [email, setEmail] = useState('')
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')
    const [invalid, setInvalid] = useState(false)

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const value = email.trim()

        if (!EMAIL_PATTERN.test(value)) {
            setInvalid(true)
            setStatus('error')
            setMessage('That address does not look quite right.')
            return
        }

        setInvalid(false)
        setStatus('sending')
        setMessage('')

        try {
            const response = await fetch('/api/newsletter', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: value, source })
            })
            const data = await response.json().catch(() => null) as { message?: string } | null

            if (!response.ok) throw new Error(data?.message ?? 'Something went wrong.')

            setStatus('done')
            setMessage(data?.message ?? 'You are on the list. The next sky guide arrives at the start of the month.')
            setEmail('')
        }
        catch (error) {
            setStatus('error')
            setMessage(error instanceof Error ? error.message : 'Something went wrong. Please try again.')
        }
    }

    return (
        <form onSubmit={handleSubmit} noValidate className={cn('w-full', className)}>
            <label htmlFor={inputId} className='label-mono text-star-3'>
                Email address
            </label>
            <div className='mt-1 flex items-end gap-3'>
                <input
                    id={inputId}
                    type='email'
                    name='email'
                    autoComplete='email'
                    inputMode='email'
                    placeholder='you@example.com'
                    value={email}
                    onChange={event => {
                        setEmail(event.target.value)
                        if (invalid) setInvalid(false)
                    }}
                    aria-invalid={invalid}
                    aria-describedby={message ? messageId : undefined}
                    className='field'
                    disabled={status === 'sending'}
                />
                <button
                    type='submit'
                    disabled={status === 'sending'}
                    className='label-mono inline-flex h-11 shrink-0 items-center gap-2 rounded-full border border-brass/60 px-5 text-star transition-colors duration-500 hover:bg-brass hover:text-ink disabled:opacity-50'
                >
                    {status === 'sending' ? 'Sending' : status === 'done' ? 'Subscribed' : 'Subscribe'}
                    {status === 'done' ? <Check aria-hidden className='size-3.5' strokeWidth={1.5} /> : <ArrowRight aria-hidden className='size-3.5' strokeWidth={1.5} />}
                </button>
            </div>
            <p
                id={messageId}
                role='status'
                aria-live='polite'
                className={cn('mt-3 min-h-[1.5rem] text-sm', status === 'error' ? 'text-signal' : 'text-star-2')}
            >
                {message}
            </p>
        </form>
    )
}
